import { useCallback, useMemo } from 'react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

interface UseAPIReturn {
  get: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse>;
  post: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse>;
  put: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse>;
  delete: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse>;
  patch: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse>;
}

const useAPI = (): UseAPIReturn => {
  // Create axios instance with base configuration - memoize to prevent re-creation
  const api = useMemo((): AxiosInstance => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    instance.interceptors.request.use(
      (config) => {
        // Add auth token if available (future enhancement)
        // const token = localStorage.getItem('auth_token');
        // if (token) {
        //   config.headers.Authorization = `Bearer ${token}`;
        // }

        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    instance.interceptors.response.use(
      (response) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('API Response Error:', error.response?.data || error.message);

        // Handle common error cases
        if (error.response?.status === 401) {
          // Unauthorized - redirect to login (future enhancement)
          console.warn('Unauthorized access - authentication required');
        } else if (error.response?.status >= 500) {
          console.error('Server error - please try again later');
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  const get = useCallback((url: string, config?: AxiosRequestConfig) => {
    return api.get(url, config);
  }, [api]);

  const post = useCallback((url: string, data?: any, config?: AxiosRequestConfig) => {
    return api.post(url, data, config);
  }, [api]);

  const put = useCallback((url: string, data?: any, config?: AxiosRequestConfig) => {
    return api.put(url, data, config);
  }, [api]);

  const deleteRequest = useCallback((url: string, config?: AxiosRequestConfig) => {
    return api.delete(url, config);
  }, [api]);

  const patch = useCallback((url: string, data?: any, config?: AxiosRequestConfig) => {
    return api.patch(url, data, config);
  }, [api]);

  return useMemo(() => ({
    get,
    post,
    put,
    delete: deleteRequest,
    patch,
  }), [get, post, put, deleteRequest, patch]);
};

export { useAPI };